import {
  queryOptions,
  useSuspenseQuery,
  useQuery,
  useMutation,
  type UseMutationOptions,
  type UseQueryOptions
} from "@tanstack/react-query";

/**
 * V5 pattern: Uses queryOptions for full type safety
 * Example usage:
 *
 * const workforceSearchQuery = createTypedQuery({
 *   queryKey: ["workforceSearch"],
 *   fetcher: async (params: { q?: string; limit: number }) => { ... },
 *   staleTime: 60000
 * });
 *
 * // In component:
 * const { data } = useQuery(workforceSearchQuery.options({ q: "test", limit: 10 }));
 *
 * // With suspense:
 * const { data } = useSuspenseQuery(workforceSearchQuery.options({ q: "test", limit: 10 }));
 *
 * // Types are fully inferred, data is never undefined in success state
 */
export function createTypedQuery<TData, TParams, TError = Error>(config: {
  queryKey: readonly unknown[];
  fetcher: (params: TParams) => Promise<TData>;
  staleTime?: number;
  retry?: number | ((failureCount: number, error: TError) => boolean);
}) {
  return {
    // queryOptions provides full type inference
    options: (params: TParams) =>
      queryOptions({
        queryKey: [...config.queryKey, params] as const,
        queryFn: () => config.fetcher(params),
        staleTime: config.staleTime,
        retry: config.retry
      }),

    // For manual cache access
    getKey: (params: TParams) => [...config.queryKey, params] as const
  };
}

// Legacy pattern - kept for backward compatibility
export function createQuery<TData, TParams, TError = Error>(config: {
  queryKey: readonly unknown[];
  fetcher: (params: TParams) => Promise<TData>;
  staleTime?: number;
  retry?: number | ((failureCount: number, error: TError) => boolean);
  placeholderData?: UseQueryOptions<TData, TError, TData>["placeholderData"];
}) {
  const baseOptions = (params: TParams) => ({
    queryKey: [...config.queryKey, params] as const,
    queryFn: () => config.fetcher(params),
    staleTime: config.staleTime,
    retry: config.retry,
    placeholderData: config.placeholderData
  });

  const useQueryHook = (args?: {
    variables?: TParams;
    enabled?: boolean;
    networkMode?: "online" | "always" | "offlineFirst";
    placeholderData?: UseQueryOptions<TData, TError, TData>["placeholderData"];
  }) => {
    return useQuery({
      ...baseOptions(args?.variables as TParams),
      enabled: args?.enabled,
      networkMode: args?.networkMode,
      placeholderData: args?.placeholderData
    });
  };

  useQueryHook.getKey = (args?: { variables: TParams }) =>
    args ? [...config.queryKey, args.variables] : config.queryKey;
  useQueryHook.queryOptions = baseOptions;

  return useQueryHook;
}

export function createSuspenseQuery<TData, TParams, _TError = Error>(config: {
  queryKey: readonly unknown[];
  fetcher: (params: TParams) => Promise<TData>;
  staleTime?: number;
}) {
  const baseOptions = (params: TParams) => ({
    queryKey: [...config.queryKey, params] as const,
    queryFn: () => config.fetcher(params),
    staleTime: config.staleTime
  });

  const useSuspenseQueryHook = (args?: {
    variables?: TParams;
    networkMode?: "online" | "always" | "offlineFirst";
  }) => {
    return useSuspenseQuery({
      ...baseOptions(args?.variables as TParams),
      networkMode: args?.networkMode
    });
  };

  useSuspenseQueryHook.getKey = (args?: { variables: TParams }) =>
    args ? [...config.queryKey, args.variables] : config.queryKey;
  useSuspenseQueryHook.queryOptions = baseOptions;

  return useSuspenseQueryHook;
}

// Replaces createMutation from react-query-kit
export function createMutation<TData, TVariables, TError = Error>(config: {
  mutationKey?: readonly unknown[];
  mutationFn: (variables: TVariables) => Promise<TData>;
}) {
  type MutationHookOptions = UseMutationOptions<TData, TError, TVariables>;

  const useMutationHook = (options?: MutationHookOptions) => {
    return useMutation({
      mutationKey: config.mutationKey,
      mutationFn: config.mutationFn,
      ...options
    });
  };

  useMutationHook.getKey = () => config.mutationKey;
  // Add a type property to help with inference
  useMutationHook._types = undefined as unknown as { variables: TVariables };

  return useMutationHook;
}

// Type helpers to replace react-query-kit
export type inferData<T> = T extends (
  ...args: unknown[]
) => (...args2: unknown[]) => { data: (infer D) | undefined }
  ? NonNullable<D>
  : T extends (...args: unknown[]) => { data: (infer D) | undefined }
    ? NonNullable<D>
    : T extends (...args: unknown[]) => (...args2: unknown[]) => infer R
      ? R extends { data: (infer D) | undefined }
        ? NonNullable<D>
        : never
      : T extends (...args: unknown[]) => infer R
        ? R extends { data: (infer D) | undefined }
          ? NonNullable<D>
          : never
        : never;
export type inferParams<T> =
  // For mutations - check for _types property first
  T extends { _types: { variables: infer V } }
    ? V
    : // For queries with variables
      T extends (args: { variables: infer P }) => unknown
      ? P
      : T extends (
            ...args: unknown[]
          ) => (args: { variables: infer P }) => unknown
        ? P
        : // For mutations - extract from mutate function
          T extends (options?: unknown) => {
              mutate: (variables: infer V) => unknown;
            }
          ? V
          : // Try to infer from UseMutationResult
            T extends (options?: unknown) => infer R
            ? R extends {
                mutate: (variables: infer V, ...args: unknown[]) => unknown;
              }
              ? V
              : never
            : never;
